<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Thông tin khách hàng'">
          <template v-slot:preview>
            <b-card no-body no-footer class="full-width border-0">
              <div v-if="isLoading">
                <div class="d-flex justify-content-center p-5">
                  <b-spinner variant="primary" label="Loading..."></b-spinner>
                </div>
              </div>
              <div v-else>
                <b-tabs content-class="mt-3" nav-class="border-bottom">
                  <b-tab title="Thông tin cá nhân" active>
                    <customer-form
                      :customer-id="customerId"
                      :is-edit-mode="true"
                      :is-view-only="isViewOnly"
                      submit-button-text="Cập nhật"
                      :initial-data="customerData"
                      @submit-success="updateCustomer"
                      @submit-error="handleError"
                    />
                  </b-tab>

                  <b-tab title="Điểm thưởng">
                    <customer-points-tab :customer-id="customerId" />
                  </b-tab>

                  <b-tab title="Lịch sử giao dịch điểm">
                    <customer-transactions-tab :customer-id="customerId" />
                  </b-tab>

                  <b-tab lazy v-if="checkPermission(['BILL_VIEW', 'BILL_WHOLESALE_VIEW', 'BILL_VIEW', 'BILL_DELIVERY_VIEW'])">
                    <template #title>
                      Lịch sử hóa đơn
                      <b-badge
                        v-if="historyCounts.billCount > 0"
                        variant="primary"
                        pill
                        class="ml-1 customer-tab-badge"
                      >{{ historyCounts.billCount }}</b-badge>
                    </template>
                    <customer-bill-history-tab
                      :customer-id="customerId"
                      :customer-phone="customerData.phoneNo"
                      :customer-name="customerData.fullName"
                    />
                  </b-tab>

                  <b-tab lazy v-if="checkPermission(['ORDER_VIEW', 'ECOM_ORDER_VIEW'])">
                    <template #title>
                      Lịch sử đơn hàng
                      <b-badge
                        v-if="historyCounts.orderCount > 0"
                        variant="primary"
                        pill
                        class="ml-1 customer-tab-badge"
                      >{{ historyCounts.orderCount }}</b-badge>
                    </template>
                    <customer-order-history-tab
                      :customer-id="customerId"
                      :customer-phone="customerData.phoneNo"
                      :customer-name="customerData.fullName"
                    />
                  </b-tab>

                  <b-tab lazy  v-if="checkPermission(['WARRANTY_VIEW', 'WARRANTY_REPORT_VIEW', 'WARRANTY_REPORT_SUMMARY_VIEW'])">
                    <template #title>
                      Lịch sử bảo hành
                      <b-badge
                        v-if="historyCounts.warrantyCount > 0"
                        variant="primary"
                        pill
                        class="ml-1 customer-tab-badge"
                      >{{ historyCounts.warrantyCount }}</b-badge>
                    </template>
                    <customer-warranty-history-tab :customer-id="customerId" />
                  </b-tab>

                  <b-tab lazy v-if="checkPermission(['TRADE_IN_VIEW'])">
                    <template #title>
                      Lịch sử thu cũ
                      <b-badge
                        v-if="historyCounts.tradeInCount > 0"
                        variant="primary"
                        pill
                        class="ml-1 customer-tab-badge"
                      >{{ historyCounts.tradeInCount }}</b-badge>
                    </template>
                    <customer-trade-in-history-tab
                      :customer-id="customerId"
                      :customer-phone="customerData.phoneNo"
                      :customer-name="customerData.fullName"
                    />
                  </b-tab>
                </b-tabs>
              </div>
            </b-card>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  getCurrentInstance,
  computed,
} from 'vue';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { makeToastSuccess, makeToastFaile, handleError} from '@/utils/common';
import CustomerForm from './components/CustomerForm.vue';
import CustomerPointsTab from './components/CustomerPointsTab.vue';
import CustomerTransactionsTab from './components/CustomerTransactionTab.vue';
import CustomerBillHistoryTab from './components/CustomerBillHistoryTab.vue';
import CustomerOrderHistoryTab from './components/CustomerOrderHistoryTab.vue';
import CustomerWarrantyHistoryTab from './components/CustomerWarrantyHistoryTab.vue';
import CustomerTradeInHistoryTab from './components/CustomerTradeInHistoryTab.vue';
import permissionMixin from '@/view/mixins/permission.mixin';

export default defineComponent({
  name: 'UpdateCustomer',
  mixins: [permissionMixin],

  components: {
    KTCodePreview,
    CustomerForm,
    CustomerPointsTab,
    CustomerTransactionsTab,
    CustomerBillHistoryTab,
    CustomerOrderHistoryTab,
    CustomerWarrantyHistoryTab,
    CustomerTradeInHistoryTab,
  },

  setup() {
    const { proxy } = getCurrentInstance();
    const customerData = ref({});
    const isLoading = ref(true);
    const customerId = ref(null);
    const historyCounts = ref({
      billCount: 0,
      orderCount: 0,
      warrantyCount: 0,
      tradeInCount: 0,
    });

    const isViewOnly = computed(() => {
      return (
        proxy.checkPermission('CUSTOMER_VIEW') && !proxy.checkPermission('CUSTOMER_UPDATE')
      );
    });

    onMounted(async () => {
      proxy.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Khách hàng', route: '/customers' },
        { title: 'Cập nhật khách hàng' },
      ]);

      customerId.value = proxy.$route.query.id;
      if (customerId.value) {
        await Promise.all([
          fetchCustomerData(customerId.value),
          fetchHistoryCounts(customerId.value),
        ]);
      }
    });

    const fetchHistoryCounts = async (id) => {
      try {
        const { data } = await ApiService.get(`/v1/customers/${id}/history-counts`);
        if (data?.data) {
          historyCounts.value = {
            billCount: data.data.billCount || 0,
            orderCount: data.data.orderCount || 0,
            warrantyCount: data.data.warrantyCount || 0,
            tradeInCount: data.data.tradeInCount || 0,
          };
        }
      } catch (error) {
        // Badge là phụ trợ — không chặn trang nếu count lỗi
      }
    };

    const fetchCustomerData = async (id) => {
      try {
        isLoading.value = true;
        const { data } = await ApiService.get(`/v1/customers/${id}`);

        if (data && data.data) {
          const formattedData = {
            ...data.data,
            dateOfBirth: data.data.dateOfBirth
              ? moment(data.data.dateOfBirth).format('DD/MM/YYYY')
              : null,
          };

          customerData.value = formattedData;
        }
      } catch (error) {
        makeToastFaile('Không thể tải thông tin khách hàng');
      } finally {
        isLoading.value = false;
      }
    };

    const updateCustomer = async (formData) => {
      try {
        const apiData = {
          ...formData,
          creditLimit: Number(formData.creditLimit) || 0,
          supplierGroupId:
            formData.supplierGroupId === '' ? null : formData.supplierGroupId,
          paymentTermsDays:
            formData.paymentTermsDays === '' ? null : formData.paymentTermsDays,
          identifyNo: formData.identifyNo?.trim() || null,
          code: formData.code?.trim() || null,
          shippingAddress1: formData.shippingAddress1?.trim() || null,
          shippingAddress2: formData.shippingAddress2?.trim() || null,
          inChargeEmployeeName: formData.inChargeEmployeeName?.trim() || null,
          dateOfBirth: formData.dateOfBirth
            ? moment(formData.dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD')
            : null,
        };

        await ApiService.put(`/v1/customers/${customerId.value}`, apiData);
        makeToastSuccess('Cập nhật khách hàng thành công');
        proxy.$router.push('/customers');
      } catch (error) {
        handleError(error);
      }
    };

    return {
      customerData,
      isLoading,
      customerId,
      historyCounts,
      isViewOnly,
      updateCustomer,
      handleError,
    };
  },
});
</script>

<style>
.nav-tabs .nav-link.active {
  font-weight: 600;
  border-bottom: 2px solid #5d78ff;
}

.customer-tab-badge {
  font-size: 0.7rem;
  vertical-align: middle;
}
</style>
